import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/useMobile";
import { trpc } from "@/lib/trpc";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Trash2, Mail, Phone, CheckCircle, XCircle, Clock, Filter, X, FileText, ArrowRight, Archive, UserCheck, Search
} from "lucide-react";
import { CSSProperties, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { hasAnyRole } from "@/lib/roles";

const DAYS_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

const STATUS_CONFIG = {
  pending: { label: "Ausstehend", variant: "secondary" as const, icon: Clock },
  confirmed: { label: "Bestätigt", variant: "default" as const, icon: CheckCircle },
  cancelled: { label: "Storniert", variant: "outline" as const, icon: XCircle },
  waitlist: { label: "Warteliste", variant: "outline" as const, icon: Clock },
};

// Status history button component
function StatusHistoryButton({ bookingId, participantName }: { bookingId: number; participantName: string }) {
  const [open, setOpen] = useState(false);
  const { data: history, isLoading } = trpc.booking.getStatusHistory.useQuery(
    { bookingId },
    { enabled: open }
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" title="Statusverlauf anzeigen">
          <FileText className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Statusverlauf</DialogTitle>
          <DialogDescription>
            Änderungshistorie für {participantName}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[400px] pr-4">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : history && history.length > 0 ? (
            <div className="space-y-4">
              {history.map((log, index) => {
                const PrevIcon = STATUS_CONFIG[log.previousStatus as keyof typeof STATUS_CONFIG]?.icon || Clock;
                const NewIcon = STATUS_CONFIG[log.newStatus as keyof typeof STATUS_CONFIG]?.icon || Clock;
                return (
                  <div key={log.id} className="flex gap-3 pb-4 border-b last:border-0">
                    <div className="flex flex-col items-center">
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      </div>
                      {index < history.length - 1 && (
                        <div className="w-px h-full bg-border mt-2" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={STATUS_CONFIG[log.previousStatus as keyof typeof STATUS_CONFIG]?.variant || "outline"} className="text-xs">
                          <PrevIcon className="h-3 w-3 mr-1" />
                          {STATUS_CONFIG[log.previousStatus as keyof typeof STATUS_CONFIG]?.label || log.previousStatus}
                        </Badge>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <Badge variant={STATUS_CONFIG[log.newStatus as keyof typeof STATUS_CONFIG]?.variant || "outline"} className="text-xs">
                          <NewIcon className="h-3 w-3 mr-1" />
                          {STATUS_CONFIG[log.newStatus as keyof typeof STATUS_CONFIG]?.label || log.newStatus}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        von <span className="font-medium text-foreground">{log.changedByName || "System"}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(log.createdAt), "dd.MM.yyyy 'um' HH:mm 'Uhr'", { locale: de })}
                      </p>
                      {log.note && (
                        <p className="text-sm mt-1 text-muted-foreground italic">
                          "{log.note}"
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Keine Statusänderungen vorhanden</p>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminBookings() {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) return <DashboardLayoutSkeleton />;
  if (!user || !hasAnyRole(user.roles, ["admin", "courseadmin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <h1 className="text-2xl font-semibold">Zugriff verweigert</h1>
          <Link href="/"><Button>Zur Startseite</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}>
      <AdminBookingsContent setSidebarWidth={setSidebarWidth} />
    </SidebarProvider>
  );
}

function AdminBookingsContent({ setSidebarWidth }: { setSidebarWidth: (width: number) => void }) {
  const isMobile = useIsMobile();
  const [location] = useLocation();

  // Parse URL parameters for initial filter values
  const urlParams = new URLSearchParams(window.location.search);
  const initialCourseId = urlParams.get('courseId');
  const initialBlockId = urlParams.get('blockId');

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [courseFilter, setCourseFilter] = useState<string>(initialCourseId || "all");
  const [blockFilter, setBlockFilter] = useState<string>(initialBlockId || "all");
  const [includeArchived, setIncludeArchived] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const utils = trpc.useUtils();
  const { data: bookings, isLoading } = trpc.booking.listAll.useQuery();
  const { data: courses } = trpc.course.list.useQuery({ includeInactive: true, includeArchived: true });

  const updateStatusMutation = trpc.booking.updateStatus.useMutation({
    onSuccess: () => {
      utils.booking.listAll.invalidate();
      toast.success("Status aktualisiert");
    },
    onError: () => toast.error("Fehler beim Aktualisieren"),
  });

  const deleteMutation = trpc.booking.delete.useMutation({
    onSuccess: () => {
      utils.booking.listAll.invalidate();
      toast.success("Anmeldung gelöscht");
    },
    onError: () => toast.error("Fehler beim Löschen"),
  });

  // Get course archive status map
  const courseArchiveMap = new Map(courses?.map(c => [c.id, c.isArchived]) || []);

  const filteredBookings = bookings?.filter(booking => {
    if (statusFilter !== "all" && booking.status !== statusFilter) return false;
    if (courseFilter !== "all" && booking.courseId !== parseInt(courseFilter)) return false;
    // Block filter
    if (blockFilter !== "all" && booking.courseBlockId !== parseInt(blockFilter)) return false;
    // Filter out archived courses unless includeArchived is true
    if (!includeArchived && courseArchiveMap.get(booking.courseId)) return false;
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const nameMatch = booking.participantName.toLowerCase().includes(query);
      const emailMatch = booking.participantEmail.toLowerCase().includes(query);
      if (!nameMatch && !emailMatch) return false;
    }
    return true;
  });

  // Filter courses for dropdown based on includeArchived
  const filteredCourses = courses?.filter(c => includeArchived || !c.isArchived);

  // Get selected course for block filter
  const selectedCourse = courses?.find(c => c.id === parseInt(courseFilter));
  const isBlockCourse = selectedCourse?.isBlockCourse;

  // Fetch blocks for the selected course if it's a block course
  const { data: courseBlocks } = trpc.courseBlock.list.useQuery(
    { courseId: parseInt(courseFilter) },
    { enabled: courseFilter !== "all" && isBlockCourse === true }
  );

  const hasActiveFilters = statusFilter !== "all" || courseFilter !== "all" || blockFilter !== "all" || includeArchived || searchQuery !== "";

  return (
    <>
      <AdminSidebar setSidebarWidth={setSidebarWidth} />

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <span className="tracking-tight text-foreground font-medium">Anmeldungen</span>
            </div>
          </div>
        )}
        <main className="flex-1 p-6">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground">Anmeldungen</h1>
            <p className="text-muted-foreground">Verwalten Sie alle Kursanmeldungen</p>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Filter className="h-4 w-4" />
                  Filter:
                </div>

                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Name oder E-Mail suchen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 w-[220px]"
                  />
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Status</SelectItem>
                    <SelectItem value="pending">Ausstehend</SelectItem>
                    <SelectItem value="confirmed">Bestätigt</SelectItem>
                    <SelectItem value="waitlist">Warteliste</SelectItem>
                    <SelectItem value="cancelled">Storniert</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={courseFilter} onValueChange={(value) => {
                  setCourseFilter(value);
                  // Reset block filter when course changes
                  setBlockFilter("all");
                }}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Kurs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Kurse</SelectItem>
                    {filteredCourses?.map(course => (
                      <SelectItem key={course.id} value={course.id.toString()}>
                        {course.name}
                        {!!course.isArchived && " (archiviert)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Block filter - only show for block courses */}
                {isBlockCourse && courseBlocks && courseBlocks.length > 0 && (
                  <Select value={blockFilter} onValueChange={setBlockFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Block" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Blöcke</SelectItem>
                      {courseBlocks.map(block => {
                        const startDate = block.sessionDates?.[0];
                        const blockLabel = startDate 
                          ? `Block ${new Date(startDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }).replace(/\.$/, '')}.`
                          : `Block ${block.id}`;
                        return (
                          <SelectItem key={block.id} value={block.id.toString()}>
                            {blockLabel}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}

                <Button
                  variant={includeArchived ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => {
                    setIncludeArchived(!includeArchived);
                    // Reset course filter if switching to non-archived and current filter is an archived course
                    if (includeArchived && courseFilter !== "all") {
                      const course = courses?.find(c => c.id === parseInt(courseFilter));
                      if (course?.isArchived) setCourseFilter("all");
                    }
                  }}
                  className="gap-1"
                >
                  <Archive className="h-4 w-4" />
                  {includeArchived ? "Ohne archivierte Kurse" : "Inkl. archivierte Kurse"}
                </Button>

                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={() => { setStatusFilter("all"); setCourseFilter("all"); setBlockFilter("all"); setIncludeArchived(false); setSearchQuery(""); }}>
                    <X className="h-4 w-4 mr-1" />
                    Filter zurücksetzen
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <Card key={i}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-1/4" />
                        <Skeleton className="h-3 w-1/3" />
                      </div>
                      <Skeleton className="h-8 w-24" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredBookings?.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">Keine Anmeldungen gefunden</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Teilnehmer</TableHead>
                      <TableHead>Kurs</TableHead>
                      <TableHead>Datum</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Verlauf</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBookings?.map(booking => {
                      const StatusIcon = STATUS_CONFIG[booking.status].icon;
                      return (
                        <TableRow key={booking.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{booking.participantName}</p>
                              <div className="flex items-center gap-3 text-sm">
                                <Link 
                                  href={`/admin/booking-history?email=${encodeURIComponent(booking.participantEmail)}`}
                                  className="flex items-center gap-1 text-muted-foreground hover:text-primary hover:underline transition-colors"
                                  title="Buchungsverlauf anzeigen"
                                >
                                  <Mail className="h-3 w-3" />
                                  {booking.participantEmail}
                                </Link>
                                {booking.participantPhone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {booking.participantPhone}
                                  </span>
                                )}
                              </div>
                              {booking.notes && (
                                <p className="text-xs text-muted-foreground mt-1 italic max-w-xs truncate" title={booking.notes}>
                                  "{booking.notes}"
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{booking.course?.name || "Unbekannt"}</p>
                              {booking.course && (
                                <p className="text-sm text-muted-foreground">
                                  {DAYS_DE[booking.course.dayOfWeek]}, {booking.course.startTime} Uhr
                                </p>
                              )}
                              {(booking as any).block && (
                                <p className="text-xs text-primary mt-0.5">
                                  Block {new Date((booking as any).block.startDate).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">
                              {format(new Date(booking.createdAt), "dd.MM.yyyy", { locale: de })}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(booking.createdAt), "HH:mm", { locale: de })} Uhr
                            </p>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={booking.status}
                              onValueChange={(value: "pending" | "confirmed" | "cancelled" | "waitlist") => 
                                updateStatusMutation.mutate({ id: booking.id, status: value })
                              }
                            >
                              <SelectTrigger className="w-[140px]">
                                <div className="flex items-center gap-2">
                                  <StatusIcon className={`h-4 w-4 ${
                                    booking.status === 'pending' ? 'text-orange-600' :
                                    booking.status === 'confirmed' ? 'text-green-600' :
                                    booking.status === 'waitlist' ? 'text-blue-600' :
                                    'text-gray-500'
                                  }`} />
                                  <span className={`${
                                    booking.status === 'pending' ? 'text-orange-600' :
                                    booking.status === 'confirmed' ? 'text-green-600' :
                                    booking.status === 'waitlist' ? 'text-blue-600' :
                                    'text-gray-500'
                                  }`}>
                                    {STATUS_CONFIG[booking.status].label}
                                  </span>
                                </div>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-orange-600" />
                                    <span>Ausstehend</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="confirmed">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                    <span>Bestätigt</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="waitlist">
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-blue-600" />
                                    <span>Warteliste</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="cancelled">
                                  <div className="flex items-center gap-2">
                                    <XCircle className="h-4 w-4 text-gray-500" />
                                    <span>Storniert</span>
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <StatusHistoryButton bookingId={booking.id} participantName={booking.participantName} />
                          </TableCell>
                          <TableCell className="text-right">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Anmeldung löschen?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Möchten Sie die Anmeldung von "{booking.participantName}" wirklich löschen?
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteMutation.mutate({ id: booking.id })} className="bg-destructive hover:bg-destructive/90">
                                    Löschen
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-4">
                {filteredBookings?.map(booking => {
                  const StatusIcon = STATUS_CONFIG[booking.status].icon;
                  return (
                    <Card key={booking.id}>
                      <CardContent className="py-4">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div>
                            <p className="font-medium">{booking.participantName}</p>
                            <p className="text-sm text-muted-foreground">{booking.participantEmail}</p>
                          </div>
                          <Badge variant={STATUS_CONFIG[booking.status].variant}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {STATUS_CONFIG[booking.status].label}
                          </Badge>
                        </div>
                        {booking.notes && (
                          <p className="text-xs text-muted-foreground mb-3 italic bg-muted/50 p-2 rounded">
                            "{booking.notes}"
                          </p>
                        )}
                        <div className="text-sm text-muted-foreground mb-3">
                          <p className="font-medium text-foreground">{booking.course?.name}</p>
                          {booking.course && (
                            <p>{DAYS_DE[booking.course.dayOfWeek]}, {booking.course.startTime} Uhr</p>
                          )}
                          {(booking as any).block && (
                            <p className="text-xs text-primary mt-0.5">
                              Block {new Date((booking as any).block.startDate).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center justify-between pt-3 border-t">
                          <Select
                            value={booking.status}
                            onValueChange={(value: "pending" | "confirmed" | "cancelled" | "waitlist") => 
                              updateStatusMutation.mutate({ id: booking.id, status: value })
                            }
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Ausstehend</SelectItem>
                              <SelectItem value="confirmed">Bestätigt</SelectItem>
                              <SelectItem value="waitlist">Warteliste</SelectItem>
                              <SelectItem value="cancelled">Storniert</SelectItem>
                            </SelectContent>
                          </Select>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 px-2 sm:px-3 text-destructive hover:text-destructive" title="Löschen">
                                <Trash2 className="h-4 w-4" />
                                <span className="hidden sm:inline ml-1.5">Löschen</span>
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Anmeldung löschen?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Möchten Sie die Anmeldung von "{booking.participantName}" wirklich löschen?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteMutation.mutate({ id: booking.id })} className="bg-destructive hover:bg-destructive/90">
                                  Löschen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <p className="mt-4 text-center text-sm text-muted-foreground">
                {filteredBookings?.length} {filteredBookings?.length === 1 ? 'Anmeldung' : 'Anmeldungen'}
                {hasActiveFilters && ` (von ${bookings?.length} gesamt)`}
              </p>
            </>
          )}
        </main>
      </SidebarInset>
    </>
  );
}
